/**
 * User Goals Manager
 *
 * Admin UI for managing user goals, templates, and bulk assignments.
 * Allows supervisors and admins to set targets for team members.
 */

import React, { useState, useMemo } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Target,
  Plus,
  Users,
  FileText,
  CheckCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  Copy,
  Edit,
  Trash2,
  UserPlus,
  Search,
} from 'lucide-react';
import {
  UserGoal,
  UserGoalCreate,
  GoalTemplate,
  UserGoalType,
  GoalPeriodType,
  GoalPriority,
  UserGoalStatus,
  GOAL_TYPE_LABELS,
  GOAL_STATUS_LABELS,
  PERIOD_TYPE_LABELS,
  PRIORITY_LABELS,
} from '@/types/userGoals';
import {
  useUserGoals,
  useTeamGoals,
  useGoalTemplates,
} from '@/hooks/useTeamManagement';
import { useTabbedChrome } from '@/components/weavein';

// ============================================================================
// TYPES
// ============================================================================

interface UserGoalsManagerProps {
  organizationId: number;
  supervisorId?: number;
  /** If provided, show goals for specific user only */
  userId?: number;
  /** If true, allow template management */
  showTemplates?: boolean;
  /** If true, allow bulk assignments */
  allowBulkAssign?: boolean;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface GoalCardProps {
  goal: UserGoal;
  locale: string;
  onEdit: (goal: UserGoal) => void;
  onUpdateProgress: (goal: UserGoal) => void;
  onDelete: (goal: UserGoal) => void;
}

function GoalCard({ goal, locale, onEdit, onUpdateProgress, onDelete }: GoalCardProps) {
  const { t } = useAppTranslation();
  const isSpanish = locale === 'es';

  const typeLabel = GOAL_TYPE_LABELS[goal.goal_type];
  const statusLabel = GOAL_STATUS_LABELS[goal.status];
  const periodLabel = PERIOD_TYPE_LABELS[goal.period_type];
  const priorityLabel = PRIORITY_LABELS[goal.priority];

  const statusColorClass = {
    gray: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  };

  return (
    <Card className="mb-3">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className={statusColorClass[statusLabel.color as keyof typeof statusColorClass]}>
                {isSpanish ? statusLabel.es : statusLabel.en}
              </Badge>
              <Badge variant="outline">
                {isSpanish ? typeLabel.es : typeLabel.en}
              </Badge>
              <Badge
                variant="outline"
                className={statusColorClass[priorityLabel.color as keyof typeof statusColorClass]}
              >
                {isSpanish ? priorityLabel.es : priorityLabel.en}
              </Badge>
            </div>

            <h4 className="font-semibold text-base">{goal.title}</h4>
            {goal.user_name && (
              <p className="text-sm text-muted-foreground">
                {t('goals.assignedTo', 'Assigned to')}: {goal.user_name}
              </p>
            )}
            {goal.program_name && (
              <p className="text-xs text-muted-foreground mt-1">
                {goal.program_name} • {isSpanish ? periodLabel.es : periodLabel.en}
              </p>
            )}

            <div className="mt-3">
              <div className="flex items-center justify-between text-sm mb-1">
                <span>
                  {goal.current_value} / {goal.target_value} {goal.unit}
                </span>
                <span
                  className={`font-bold ${
                    goal.progress_percentage >= 100
                      ? 'text-green-600'
                      : goal.progress_percentage >= 75
                      ? 'text-blue-600'
                      : goal.progress_percentage >= 50
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                >
                  {goal.progress_percentage}%
                </span>
              </div>
              <Progress
                value={Math.min(goal.progress_percentage, 100)}
                className="h-2"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <Button size="sm" variant="outline" onClick={() => onUpdateProgress(goal)}>
              <TrendingUp className="w-4 h-4 mr-1" />
              {t('goals.updateProgress', 'Update')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onEdit(goal)}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:text-red-700"
              onClick={() => onDelete(goal)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function UserGoalsManager({
  organizationId,
  supervisorId,
  userId,
  showTemplates = true,
  allowBulkAssign = true,
}: UserGoalsManagerProps) {
  const { t, currentLanguage } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const locale = currentLanguage || 'en';
  const isSpanish = locale === 'es';

  const [activeTab, setActiveTab] = useState('goals');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<UserGoalStatus | 'all'>('all');
  const [filterType, setFilterType] = useState<UserGoalType | 'all'>('all');

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<UserGoal | null>(null);

  // Form state for new goal
  const [newGoal, setNewGoal] = useState<Partial<UserGoalCreate>>({
    goal_type: 'enrollment',
    period_type: 'monthly',
    priority: 'medium',
    target_value: 10,
    unit: 'count',
  });
  const [progressUpdate, setProgressUpdate] = useState({ value: 0, notes: '' });

  // Org-wide Goals Management is not yet backed by a live aggregation endpoint
  // (useUserGoals fetches per-user only, and this screen renders without a userId).
  // Render from an empty dataset until that endpoint exists (tracked as a separate
  // feature). Never render fabricated sample data: it previously surfaced people
  // from other organizations on every org's Goals Management screen (TRUS-263).
  const goals: UserGoal[] = [];
  const templates: GoalTemplate[] = [];

  // Filter goals
  const filteredGoals = useMemo(() => {
    return goals.filter((goal) => {
      if (filterStatus !== 'all' && goal.status !== filterStatus) return false;
      if (filterType !== 'all' && goal.goal_type !== filterType) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          goal.title.toLowerCase().includes(query) ||
          goal.user_name?.toLowerCase().includes(query) ||
          goal.program_name?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [goals, filterStatus, filterType, searchQuery]);

  // Summary stats
  const stats = useMemo(() => {
    const total = goals.length;
    const completed = goals.filter((g) => g.status === 'completed' || g.status === 'exceeded').length;
    const onTrack = goals.filter((g) => g.status === 'on_track' || g.status === 'in_progress').length;
    const atRisk = goals.filter((g) => g.status === 'at_risk' || g.status === 'behind').length;
    const avgProgress = total > 0
      ? Math.round(goals.reduce((sum, g) => sum + g.progress_percentage, 0) / total)
      : 0;
    return { total, completed, onTrack, atRisk, avgProgress };
  }, [goals]);

  const handleCreateGoal = () => {
    // In production: await createGoal(newGoal as UserGoalCreate);
    console.log('Create goal:', newGoal);
    setShowCreateDialog(false);
    setNewGoal({
      goal_type: 'enrollment',
      period_type: 'monthly',
      priority: 'medium',
      target_value: 10,
      unit: 'count',
    });
  };

  const handleUpdateProgress = () => {
    if (!selectedGoal) return;
    // In production: await updateProgress(selectedGoal.id, progressUpdate.value, progressUpdate.notes);
    console.log('Update progress:', selectedGoal.id, progressUpdate);
    setShowProgressDialog(false);
    setSelectedGoal(null);
    setProgressUpdate({ value: 0, notes: '' });
  };

  const handleEditGoal = (goal: UserGoal) => {
    setSelectedGoal(goal);
    setNewGoal({
      user_id: goal.user_id,
      program_id: goal.program_id,
      goal_type: goal.goal_type,
      title: goal.title,
      description: goal.description,
      target_value: goal.target_value,
      unit: goal.unit,
      period_type: goal.period_type,
      priority: goal.priority,
    });
    setShowCreateDialog(true);
  };

  const handleOpenProgressDialog = (goal: UserGoal) => {
    setSelectedGoal(goal);
    setProgressUpdate({ value: goal.current_value, notes: '' });
    setShowProgressDialog(true);
  };

  const handleDeleteGoal = (goal: UserGoal) => {
    if (confirm(t('goals.confirmDelete', 'Are you sure you want to delete this goal?'))) {
      // In production: await deleteGoal(goal.id);
      console.log('Delete goal:', goal.id);
    }
  };

  const handleCreateFromTemplate = (template: GoalTemplate) => {
    setNewGoal({
      goal_type: template.goal_type,
      title: template.title_template,
      description: template.description_template,
      target_value: template.default_target_value,
      unit: template.unit,
      period_type: template.default_period_type,
      priority: template.default_priority,
      program_id: template.program_id,
    });
    setShowCreateDialog(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && (
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Target className="w-6 h-6" />
            {t('goals.manager.title', 'Goals Management')}
          </h2>
          )}
          <p className="text-muted-foreground">
            {t('goals.manager.description', 'Assign and track performance goals for team members')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {allowBulkAssign && (
            <Button variant="outline" onClick={() => setShowBulkAssignDialog(true)}>
              <Users className="w-4 h-4 mr-2" />
              {t('goals.bulkAssign', 'Bulk Assign')}
            </Button>
          )}
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('goals.createGoal', 'Create Goal')}
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">
              {t('goals.stats.total', 'Total Goals')}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-green-600">{stats.completed}</div>
            <div className="text-sm text-muted-foreground">
              {t('goals.stats.completed', 'Completed')}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{stats.onTrack}</div>
            <div className="text-sm text-muted-foreground">
              {t('goals.stats.onTrack', 'On Track')}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-red-600">{stats.atRisk}</div>
            <div className="text-sm text-muted-foreground">
              {t('goals.stats.atRisk', 'At Risk')}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold">{stats.avgProgress}%</div>
            <div className="text-sm text-muted-foreground">
              {t('goals.stats.avgProgress', 'Avg Progress')}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="goals">
            <Target className="w-4 h-4 mr-2" />
            {t('goals.tabs.activeGoals', 'Active Goals')}
          </TabsTrigger>
          {showTemplates && (
            <TabsTrigger value="templates">
              <Copy className="w-4 h-4 mr-2" />
              {t('goals.tabs.templates', 'Templates')}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Goals Tab */}
        <TabsContent value="goals" className="mt-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder={t('goals.searchPlaceholder', 'Search goals...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select
              value={filterStatus}
              onValueChange={(value) => setFilterStatus(value as UserGoalStatus | 'all')}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder={t('goals.filterStatus', 'Status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
                {Object.entries(GOAL_STATUS_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {isSpanish ? label.es : label.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filterType}
              onValueChange={(value) => setFilterType(value as UserGoalType | 'all')}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder={t('goals.filterType', 'Type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
                {Object.entries(GOAL_TYPE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {isSpanish ? label.es : label.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Goals List */}
          {filteredGoals.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {t('goals.noGoals', 'No goals found')}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {t('goals.noGoalsDescription', 'Create a new goal to get started')}
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('goals.createGoal', 'Create Goal')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div>
              {filteredGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  locale={locale}
                  onEdit={handleEditGoal}
                  onUpdateProgress={handleOpenProgressDialog}
                  onDelete={handleDeleteGoal}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        {showTemplates && (
          <TabsContent value="templates" className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-muted-foreground">
                {t('goals.templates.description', 'Reusable goal templates for quick assignment')}
              </p>
              <Button variant="outline" onClick={() => setShowTemplateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('goals.templates.create', 'Create Template')}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => {
                const typeLabel = GOAL_TYPE_LABELS[template.goal_type];
                const periodLabel = PERIOD_TYPE_LABELS[template.default_period_type];
                const priorityLabel = PRIORITY_LABELS[template.default_priority];

                return (
                  <Card key={template.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">
                          {isSpanish ? typeLabel.es : typeLabel.en}
                        </Badge>
                        <Badge variant={template.is_active ? 'default' : 'secondary'}>
                          {template.is_active
                            ? t('common.active', 'Active')
                            : t('common.inactive', 'Inactive')}
                        </Badge>
                      </div>
                      <CardTitle className="text-base mt-2">{template.name}</CardTitle>
                      <CardDescription>{template.description_template}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="flex justify-between">
                          <span>{t('goals.target', 'Target')}:</span>
                          <span className="font-medium">
                            {template.default_target_value} {template.unit}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('goals.period', 'Period')}:</span>
                          <span>{isSpanish ? periodLabel.es : periodLabel.en}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('goals.priority', 'Priority')}:</span>
                          <span>{isSpanish ? priorityLabel.es : priorityLabel.en}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={() => handleCreateFromTemplate(template)}
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          {t('goals.useTemplate', 'Use')}
                        </Button>
                        <Button size="sm" variant="ghost">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Create/Edit Goal Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedGoal
                ? t('goals.dialog.editTitle', 'Edit Goal')
                : t('goals.dialog.createTitle', 'Create New Goal')}
            </DialogTitle>
            <DialogDescription>
              {t('goals.dialog.description', 'Set a performance target for a team member')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('goals.form.title', 'Goal Title')}</Label>
              <Input
                value={newGoal.title || ''}
                onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                placeholder={t('goals.form.titlePlaceholder', 'e.g., Monthly Enrollments')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('goals.form.description', 'Description')} ({t('common.optional', 'optional')})</Label>
              <Input
                value={newGoal.description || ''}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                placeholder={t('goals.form.descriptionPlaceholder', 'What this goal measures')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('goals.form.type', 'Goal Type')}</Label>
                <Select
                  value={newGoal.goal_type}
                  onValueChange={(value) =>
                    setNewGoal({ ...newGoal, goal_type: value as UserGoalType })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(GOAL_TYPE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {isSpanish ? label.es : label.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('goals.form.period', 'Period')}</Label>
                <Select
                  value={newGoal.period_type}
                  onValueChange={(value) =>
                    setNewGoal({ ...newGoal, period_type: value as GoalPeriodType })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PERIOD_TYPE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {isSpanish ? label.es : label.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('goals.form.target', 'Target Value')}</Label>
                <Input
                  type="number"
                  min={1}
                  value={newGoal.target_value || ''}
                  onChange={(e) =>
                    setNewGoal({ ...newGoal, target_value: parseInt(e.target.value) || 0 })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>{t('goals.form.unit', 'Unit')}</Label>
                <Input
                  value={newGoal.unit || ''}
                  onChange={(e) => setNewGoal({ ...newGoal, unit: e.target.value })}
                  placeholder={t('goals.form.unitPlaceholder', 'e.g., members, calls')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('goals.form.priority', 'Priority')}</Label>
              <Select
                value={newGoal.priority}
                onValueChange={(value) =>
                  setNewGoal({ ...newGoal, priority: value as GoalPriority })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {isSpanish ? label.es : label.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleCreateGoal}>
              {selectedGoal
                ? t('common.save', 'Save')
                : t('goals.dialog.create', 'Create Goal')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Progress Dialog */}
      <Dialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('goals.progress.title', 'Update Progress')}</DialogTitle>
            <DialogDescription>
              {selectedGoal?.title}
            </DialogDescription>
          </DialogHeader>

          {selectedGoal && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span>{t('goals.progress.current', 'Current Progress')}</span>
                  <span className="font-bold">
                    {selectedGoal.current_value} / {selectedGoal.target_value} {selectedGoal.unit}
                  </span>
                </div>
                <Progress value={selectedGoal.progress_percentage} className="h-2" />
              </div>

              <div className="space-y-2">
                <Label>{t('goals.progress.newValue', 'New Value')}</Label>
                <Input
                  type="number"
                  min={0}
                  max={selectedGoal.target_value * 2}
                  value={progressUpdate.value}
                  onChange={(e) =>
                    setProgressUpdate({
                      ...progressUpdate,
                      value: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>{t('goals.progress.notes', 'Notes')} ({t('common.optional', 'optional')})</Label>
                <Input
                  value={progressUpdate.notes}
                  onChange={(e) =>
                    setProgressUpdate({ ...progressUpdate, notes: e.target.value })
                  }
                  placeholder={t('goals.progress.notesPlaceholder', 'Add a note about this update')}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProgressDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleUpdateProgress}>
              {t('goals.progress.update', 'Update Progress')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssignDialog} onOpenChange={setShowBulkAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('goals.bulk.title', 'Bulk Assign Goals')}</DialogTitle>
            <DialogDescription>
              {t('goals.bulk.description', 'Assign the same goal to multiple team members')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 text-center text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t('goals.bulk.selectMembers', 'Select team members and configure goal settings')}</p>
            <p className="text-sm mt-2">
              {t('goals.bulk.comingSoon', 'Team member selection will be available when backend is connected')}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAssignDialog(false)}>
              {t('common.close', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UserGoalsManager;
